import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Terminal, Cpu, Brain, Building2, Smartphone, Cloud, ArrowRight, ChevronLeft, ChevronRight, Network } from "lucide-react";

const services = [
  {
    id: "linux",
    icon: Terminal,
    title: "Linux Support",
    description: "Administration, patching, hardening, backups, monitoring, and incident response for Ubuntu/RHEL-based stacks.",
    features: ["Server baseline & CIS-style hardening", "Observability: logs/metrics/alerts", "Runbooks + on-call workflows"],
    color: "#30D158"
  },
  {
    id: "hpc",
    icon: Cpu,
    title: "HPC Support",
    description: "Cluster operations and performance engineering — stable compute for science, engineering, and AI workloads.",
    features: ["Schedulers (e.g., Slurm) & job workflows", "Performance tuning & profiling", "Reliability + capacity planning"],
    color: "#2997FF"
  },
  {
    id: "ai",
    icon: Brain,
    title: "AI Enablement",
    description: "From pilots to production — data pipelines, model deployment, and governance aligned to business outcomes.",
    features: ["MLOps + CI/CD for models", "RAG systems and enterprise search", "Security, privacy, and controls"],
    color: "#BF5AF2"
  },
  {
    id: "digital",
    icon: Building2,
    title: "Digital Transformation",
    description: "Platform modernization across cloud, automation, and integration to increase speed, reliability, and insight.",
    features: ["Cloud migration & modernization", "DevOps automation", "API-first integration"],
    color: "#FF9F0A"
  },
  {
    id: "app",
    icon: Smartphone,
    title: "App Development",
    description: "Mobile and web applications built with modern architectures and strong UX.",
    features: ["Cross-platform & native delivery", "Backend APIs and integrations", "Testing + release readiness"],
    color: "#FF375F"
  },
  {
    id: "cloud",
    icon: Cloud,
    title: "Cloud & DevOps",
    description: "IaC, CI/CD, containerization, and secure deployments on AWS and beyond.",
    features: ["Docker/Kubernetes foundations", "Cost optimization", "Security guardrails"],
    color: "#64D2FF"
  },
  {
    id: "dpi",
    icon: Network,
    title: "Digital Public Infrastructure",
    description: "Building the foundational digital rails that enable governments, institutions, and economies to deliver services at population scale with security, interoperability, and inclusion at the core.",
    features: [
      "Identity & authentication systems (national ID, eKYC)",
      "Payment rails & financial inclusion platforms",
      "Data exchange & consent management frameworks",
      "Open APIs for government service delivery",
      "Interoperability standards & protocol design"
    ],
    longDescription: "Digital Public Infrastructure (DPI) represents the next generation of digital systems that serve as shared, foundational building blocks for society. Like physical infrastructure—roads, electricity grids, and water systems—DPI creates the digital rails upon which governments, businesses, and citizens can build and transact securely.",
    approach: [
      "Population-scale architecture designed for billions of transactions",
      "Privacy-by-design with user consent and data protection",
      "Open standards enabling ecosystem innovation",
      "Resilient, secure, and auditable systems",
      "Inclusive design for last-mile accessibility"
    ],
    color: "#FFD60A"
  }
];

const ServicesPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const location = useLocation();

  // Handle hash navigation from dropdown
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      const index = services.findIndex(s => s.id === hash);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [location.hash]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? services.length - 1 : prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === services.length - 1 ? 0 : prev + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentService = services[currentIndex];
  const Icon = currentService.icon;

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Main Content */}
      <section className="py-16 md:py-24 bg-black min-h-[100dvh] flex items-center">
        <div className="max-w-[1200px] mx-auto px-6 w-full">
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center gap-8">
            {/* Left Arrow */}
            <button
              onClick={goToPrevious}
              className="flex-shrink-0 w-14 h-14 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] hover:text-[#F5F5F7] hover:border-[#2997FF]/50 transition-all"
              data-testid="services-prev"
              aria-label="Previous service"
            >
              <ChevronLeft size={28} />
            </button>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="text-center"
                >
                  {/* Icon */}
                  <div
                    className="w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${currentService.color}20` }}
                  >
                    <Icon size={40} style={{ color: currentService.color }} />
                  </div>

                  {/* Title */}
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#F5F5F7] tracking-tight mb-6" data-testid="services-title">
                    {currentService.title}
                  </h1>

                  {/* Description */}
                  <p className="text-lg md:text-xl text-[#86868B] max-w-2xl mx-auto mb-10 leading-relaxed">
                    {currentService.description}
                  </p>

                  {/* Features */}
                  <div className="flex justify-center">
                    <div className="flex flex-col gap-3 text-left">
                      {currentService.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-[#86868B]">
                          <div
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: currentService.color }}
                          />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex items-center justify-center gap-2 mt-12">
                {services.map((service, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex ? "w-6" : "hover:bg-[#86868B]"
                    }`}
                    style={{
                      backgroundColor: index === currentIndex ? services[index].color : 'rgba(134, 134, 139, 0.5)'
                    }}
                    aria-label={`Go to ${service.title}`}
                  />
                ))}
              </div>
            </div>

            {/* Right Arrow */}
            <button
              onClick={goToNext}
              className="flex-shrink-0 w-14 h-14 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] hover:text-[#F5F5F7] hover:border-[#2997FF]/50 transition-all"
              data-testid="services-next"
              aria-label="Next service"
            >
              <ChevronRight size={28} />
            </button>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden">
            {/* Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-center min-h-[400px]"
              >
                {/* Icon */}
                <div
                  className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: `${currentService.color}20` }}
                >
                  <Icon size={32} style={{ color: currentService.color }} />
                </div>

                {/* Title */}
                <h1 className="text-3xl font-semibold text-[#F5F5F7] tracking-tight mb-4">
                  {currentService.title}
                </h1>

                {/* Description */}
                <p className="text-base text-[#86868B] mb-6 leading-relaxed">
                  {currentService.description}
                </p>

                {/* Features */}
                <div className="flex justify-center mb-8">
                  <div className="flex flex-col gap-2 text-left">
                    {currentService.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-sm text-[#86868B]">
                        <div
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: currentService.color }}
                        />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-center gap-6 mt-8">
              <button
                onClick={goToPrevious}
                className="w-12 h-12 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] active:text-[#F5F5F7] active:border-[#2997FF]/50 transition-all"
                aria-label="Previous service"
              >
                <ChevronLeft size={24} />
              </button>

              {/* Dots */}
              <div className="flex items-center gap-2">
                {services.map((service, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex ? "w-4" : ""
                    }`}
                    style={{
                      backgroundColor: index === currentIndex ? services[index].color : 'rgba(134, 134, 139, 0.5)'
                    }}
                    aria-label={`Go to ${service.title}`}
                  />
                ))}
              </div>

              <button
                onClick={goToNext}
                className="w-12 h-12 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] active:text-[#F5F5F7] active:border-[#2997FF]/50 transition-all"
                aria-label="Next service"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#1C1C1E]">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold text-[#F5F5F7] tracking-tight mb-4">
              Want a quick call?
            </h2>
            <p className="text-lg text-[#86868B] max-w-2xl mx-auto mb-8">
              Let's discuss how we can help your organization.
            </p>
            <Link
              to="/contact"
              className="btn-primary px-8 py-4 rounded-full text-base font-medium inline-flex items-center gap-2"
              data-testid="services-cta"
            >
              Get in Touch
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
